import React, { useState } from 'react';
import { t } from 'i18next';

import {
  useGetDailyRewardStatusQuery,
  useClaimDailyRewardMutation,
} from '@app/store/slices/dailyReward/dailyRewardApi';
import { useFlyingCookie } from '@app/shared/hooks/useFlyingCookie';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { getHeaderCookieBalanceElementRect } from '@app/shared/utils/layout';
import { isFetchDOMError, telegramNotify } from '@app/shared/utils/common';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { commonActions } from '@app/store/slices/common/commonSlice';
import notification from '@app/shared/utils/notification';

export function useDailyRewardModal() {
  const dispatch = useAppDispatch();
  const { data: dailyRewardStatus } = useGetDailyRewardStatusQuery();
  const [claimDailyReward, { isLoading: isClaimingDailyReward }] = useClaimDailyRewardMutation();
  const { cookie: flyingCookie, animateCookie: animateFlyingCookie } = useFlyingCookie();
  const { handleFetchError } = useApiErrorHandler();
  const [isCloseWindow, setCloseWindow] = useState(false);

  const { isPredictionModalOpen } = useAppSelector((state) => state.common);

  const setIsPredictionModalOpen = (value: boolean) =>
    dispatch(commonActions.setIsPredictionModalOpen(value));

  const handleDailyRewardAccept = async (event: React.MouseEvent<HTMLElement>, reward: number) => {
    if (isClaimingDailyReward) {
      return;
    }

    try {
      await claimDailyReward(reward).unwrap();

      const sourceElementRect =
        event.target instanceof HTMLElement ? event.target.getBoundingClientRect() : undefined;
      const headerCookieBalanceElementRect = getHeaderCookieBalanceElementRect();

      telegramNotify('success');
      animateFlyingCookie(sourceElementRect, headerCookieBalanceElementRect);
      setIsPredictionModalOpen(true);
    } catch (err) {
      if (isFetchDOMError(err) && err.data.message === 'BALANCE_OPERATION_ALREADY_APPLIED') {
        notification.error(t('errors.daily_reward_already_applied'));
      } else {
        handleFetchError(err);
      }
      setCloseWindow(true);
    }
  };

  return {
    dailyRewardStatus,
    isClaimingDailyReward,
    handleDailyRewardAccept,
    flyingCookie,
    isPredictionModalOpen,
    setIsPredictionModalOpen,
    isCloseWindow,
  };
}
